/**
 * Notification Sound and Browser Push Utilities
 * Handles audio notifications and browser push notifications
 */

// Notification sound - using Web Audio API for better compatibility
let audioContext = null;

const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Create a simple notification beep sound using Web Audio API
const createBeepSound = (frequency = 800, duration = 150, volume = 0.3) => {
  try {
    const ctx = initAudioContext();
    
    // Resume audio context if suspended (required by some browsers)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
    
    return true;
  } catch (error) {
    console.error('Error playing notification sound:', error);
    return false;
  }
};

// Play notification sound
export const playNotificationSound = (type = 'default') => {
  const settings = getNotificationSettings();
  const volume = settings.soundVolume || 0.3;
  
  switch (type) {
    case 'message':
      // Double beep for messages
      createBeepSound(880, 100, volume * 0.7);
      setTimeout(() => createBeepSound(1100, 100, volume * 0.7), 120);
      break;
    case 'appointment':
      // Triple ascending beep for appointments
      createBeepSound(600, 80, volume * 0.8);
      setTimeout(() => createBeepSound(800, 80, volume * 0.8), 100);
      setTimeout(() => createBeepSound(1000, 120, volume * 0.8), 200);
      break;
    case 'urgent':
      // Urgent sound - faster beeps
      createBeepSound(1000, 80, volume);
      setTimeout(() => createBeepSound(1200, 80, volume), 100);
      setTimeout(() => createBeepSound(1000, 80, volume), 200);
      setTimeout(() => createBeepSound(1200, 120, volume), 300);
      break;
    case 'subscription-upgrade':
    case 'subscription-switch':
      // These use WAV files from public/sounds
      // Handled separately by playSubscriptionSound
      break;
    default:
      // Single beep for default notifications
      createBeepSound(800, 150, volume * 0.8);
  }
};

// Play subscription change sound (uses WAV files)
export const playSubscriptionSound = (isUpgrade = true) => {
  const settings = getNotificationSettings();
  
  // Check if sounds are enabled globally and for subscriptions
  if (!settings.soundEnabled || !settings.subscriptionSound) {
    return false;
  }
  
  try {
    const soundFile = isUpgrade ? '/sounds/upgrade.wav' : '/sounds/switch.wav';
    const audio = new Audio(soundFile);
    audio.volume = settings.soundVolume || 0.5;
    audio.play().catch(e => console.log('Sound play prevented:', e));
    return true;
  } catch (e) {
    console.log('Sound not available:', e);
    return false;
  }
};

// Check if browser supports notifications
export const supportsNotifications = () => {
  return 'Notification' in window;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!supportsNotifications()) {
    console.log('Browser does not support notifications');
    return 'denied';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }
  
  return Notification.permission;
};

// Check if tab is currently visible
export const isTabVisible = () => {
  return document.visibilityState === 'visible';
};

// Show browser push notification
export const showBrowserNotification = (title, options = {}) => {
  if (!supportsNotifications()) {
    return null;
  }
  
  if (Notification.permission !== 'granted') {
    return null;
  }
  
  // Don't show if tab is visible
  if (isTabVisible() && !options.forceShow) {
    return null;
  }
  
  const defaultOptions = {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    silent: false, // Let browser handle sound
    tag: 'mymedmember-notification',
    renotify: true,
    ...options
  };
  
  try {
    const notification = new Notification(title, defaultOptions);
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Handle click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      notification.close();
      
      if (options.onClick) {
        options.onClick(event);
      }
    };
    
    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};

// Notification settings stored in localStorage
const NOTIFICATION_SETTINGS_KEY = 'notification_settings';

export const getNotificationSettings = () => {
  const defaults = {
    soundEnabled: true,
    pushEnabled: true,
    soundVolume: 0.3,
    messageSound: true,
    appointmentSound: true,
    urgentSound: true,
    subscriptionSound: true  // New: subscription change sounds
  };
  
  try {
    const stored = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
    if (stored) {
      return { ...defaults, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error reading notification settings:', e);
  }
  
  return defaults;
};

export const saveNotificationSettings = (settings) => {
  try {
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('Error saving notification settings:', e);
    return false;
  }
};

// Combined notification function
export const triggerNotification = (title, message, type = 'default', options = {}) => {
  const settings = getNotificationSettings();
  
  // Play sound if enabled
  if (settings.soundEnabled) {
    const shouldPlaySound = 
      (type === 'message' && settings.messageSound) ||
      (type === 'appointment' && settings.appointmentSound) ||
      (type === 'urgent' && settings.urgentSound) ||
      type === 'default';
    
    if (shouldPlaySound) {
      playNotificationSound(type);
    }
  }
  
  // Show browser notification if enabled and tab not visible
  if (settings.pushEnabled && !isTabVisible()) {
    showBrowserNotification(title, {
      body: message,
      tag: `${type}-${Date.now()}`,
      ...options
    });
  }
};

export default {
  playNotificationSound,
  playSubscriptionSound,
  supportsNotifications,
  requestNotificationPermission,
  showBrowserNotification,
  isTabVisible,
  getNotificationSettings,
  saveNotificationSettings,
  triggerNotification
};
